/**
 * Name tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_meaning`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const nameTattoosPack: ToilePack = {
    slug: 'name-tattoos',
    category: 'tattoo',
    name: 'Name tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'script', label: 'Script name', subject: 'the name "Maya" written in one flowing line of joined cursive, the final letter ending in a small heart'},
        {key: 'heart', label: 'Name in heart', subject: 'the name "Maya" lettered inside a small outline heart, the letters filling its width'},
        {key: 'stem', label: 'Name on stem', subject: 'the name "Maya" written along the stem of a single wildflower, the letters becoming part of the stem'},
    ],
    styles: [
        {key: 'script', label: 'Fine script', style: 'a fine single-needle script hand, hairline connecting strokes, thin upstrokes against slightly heavier downstrokes', palette: 'black ink only', framing: 'The design centered on the sheet with a wide white margin on every side'},
        {key: 'brush', label: 'Brush script', style: 'a bold brush-script hand, thick tapered strokes with a solid black fill and sharp pointed ends', palette: 'black ink only', framing: 'The design centered on the sheet with a wide white margin on every side'},
        {key: 'serif', label: 'Serif', style: 'an engraved serif face cut by hand, sharp bracketed serifs and high contrast between thick stems and hairlines', palette: 'black ink only', framing: 'The design centered on the sheet with a wide white margin on every side'},
    ],
    directions: ['red ink', 'ribcage', 'two names'],
    directionHint: 'try: red ink',
    seo: {
        title: 'Name tattoo designs: script, heart, flower stem',
        metaDescription: 'Nine name tattoo designs: the name Maya in a script, inside a heart and on a flower stem, in three lettering styles.',
        h1: 'Name tattoo designs: script, heart, and flower stem',
        tagline: 'The sample name Maya in three layouts and three lettering styles.',
        intro: "People who tattoo a child's, a partner's or a parent's name want to see how it will look before they choose. The sheet letters the sample name Maya in a flowing script, inside a small heart and along the stem of a wildflower, each in fine script, brush script and serif. Type another name, and the same layouts and lettering styles carry it.",
        useCases: [
            'seeing a name in a script before choosing the lettering',
            'a layout sheet to show the tattoo artist',
            'a heart or flower frame for a name on the forearm or wrist',
        ],
        faq: [
            {q: 'Who is Maya in these pictures?', a: 'Maya is only the sample name. The designs show how a name looks in each layout; the name is not one the designs are made for.'},
            {q: 'What is the difference between the lettering styles?', a: 'Fine script uses hairline connecting strokes, brush script uses thick tapered strokes with a solid fill, and serif uses sharp bracketed letters with high contrast.'},
            {q: 'Can I use my own name?', a: 'Yes. Type the name you want and the three layouts and three lettering styles are applied to it. The result is a request, not a promised image.'},
            {q: 'Are these shown on skin?', a: 'No. Each design is shown alone on white paper, not on an arm or a body.'},
        ],
    },
}
